// components/EditUserModalForm/DynamicFieldRenderer.jsx
import React from 'react';
import { TextField, Checkbox, Select, MenuItem, Tooltip } from '@mui/material';
import InputMask from 'react-input-mask';
import * as S from '../styles';
import phoneMaskArray from 'js/components/DesignSystem/phoneMaskArray.json';
import { maskCEP } from 'js/library/utils/helpers';

const UFs = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const DynamicFieldRenderer = ({
  field,
  partnerData,
  setPartnerData,
  handleChange,
  handleChangeCellphone,
  handleChangeCheck,
  treatmentCep,
  currentCountry,
  handleCountryChange,
  cellphone,
  inputMaskCellphone,
  clubForms,
}) => {
  const { inputType, fieldType, fieldName, label, required, inputMask } = field;
  const fieldValue = partnerData[fieldName] || '';

  const handleTextChange = (e) => {
    setPartnerData((prev) => ({ ...prev, [fieldName]: e.target.value }));
  };

  if (['text', 'cpf', 'cnpj'].includes(inputType)) {
    if (fieldType === 'cellPhone') {
      return (
        <React.Fragment>
          {label}
          <TextField
            fullWidth
            disabled
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <select
                  disabled
                  value={currentCountry}
                  onChange={handleCountryChange}
                  style={{
                    fontFamily: "NotoColorEmojiLimited, 'Source Sans Pro', sans-serif",
                    height: '100%',
                    fontSize: '17px',
                    color: '#383838d6',
                    border: 'none',
                    outline: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    marginLeft: '5px',
                  }}
                >
                  {phoneMaskArray.map((country) => (
                    <option key={country.id} value={[country.ddd, country.mask, country.fullMask]}>
                      {country.emoji} {country.initials} {country.ddd}
                    </option>
                  ))}
                </select>
              ),
              inputComponent: InputMask,
              inputProps: {
                mask: inputMaskCellphone,
                value: cellphone,
                placeholder: inputMaskCellphone,
                onChange: (e) => handleChangeCellphone(e, fieldName),
              },
            }}
          />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <span>
          {label}
          {fieldName === clubForms.tokenType && <span> (Obrigatório)</span>}
        </span>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          InputProps={{
            inputComponent: InputMask,
            inputProps: {
              mask: inputMask,
              value: fieldValue || '',
              placeholder: inputMask,
              onChange: handleTextChange,
            },
          }}
        />
      </React.Fragment>
    );
  }

  if (inputType === 'email') {
    return (
      <React.Fragment>
        {label}
        <TextField
          placeholder={label}
          value={fieldValue}
          onChange={handleTextChange}
          size="small"
          fullWidth
        />
      </React.Fragment>
    );
  }

  if (inputType === 'checkbox') {
    return (
      <React.Fragment>
        {label}
        <Checkbox
          checked={!!fieldValue?.accept || !!fieldValue}
          onChange={() => handleChangeCheck(fieldName)}
        />
      </React.Fragment>
    );
  }

  if (inputType === 'date') {
    return (
      <React.Fragment>
        {label}
        <TextField
          placeholder={label}
          value={fieldValue}
          type="date"
          onChange={handleTextChange}
          size="small"
          fullWidth
        />
      </React.Fragment>
    );
  }

  if (inputType === 'address') {
    return (
      <>
        {label}
        <S.ContentLabelAddress>
          <label style={{ fontSize: 12 }}>CEP</label>
          <TextField
            id={label.replace(/\\s+/g, '')}
            placeholder="99999-999"
            value={maskCEP(partnerData.zipCode || '')}
            onChange={treatmentCep}
            size="small"
            fullWidth
          />
        </S.ContentLabelAddress>

        {partnerData.zipCode?.length >= 8 && (
          <>
            <S.ContentInfoAddress>
              <S.ContentLabelAddress>
                <label style={{ fontSize: 12 }}>Rua</label>
                <TextField
                  placeholder="Rua"
                  value={partnerData.streetAve || ''}
                  onChange={(e) =>
                    setPartnerData((prev) => ({ ...prev, streetAve: e.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              </S.ContentLabelAddress>
              <S.ContentLabelAddress>
                <label style={{ fontSize: 12 }}>Número</label>
                <TextField
                  placeholder="Número"
                  value={partnerData.streetNumber || ''}
                  onChange={(e) =>
                    setPartnerData((prev) => ({ ...prev, streetNumber: e.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              </S.ContentLabelAddress>
            </S.ContentInfoAddress>

            <S.ContentInfoAddress>
              <S.ContentLabelAddress>
                <label style={{ fontSize: 12 }}>Bairro</label>
                <TextField
                  placeholder="Bairro"
                  value={partnerData.neighborhood || ''}
                  onChange={(e) =>
                    setPartnerData((prev) => ({ ...prev, neighborhood: e.target.value }))
                  }
                  size="small"
                  fullWidth
                />
              </S.ContentLabelAddress>
              <S.ContentLabelAddress>
                <label style={{ fontSize: 12 }}>Cidade</label>
                <TextField
                  placeholder="Cidade"
                  value={partnerData.city || ''}
                  onChange={(e) => setPartnerData((prev) => ({ ...prev, city: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </S.ContentLabelAddress>
              <S.ContentLabelAddress>
                <label style={{ fontSize: 12 }}>UF</label>
                <Select
                  value={partnerData.state || ''}
                  onChange={(e) => setPartnerData((prev) => ({ ...prev, state: e.target.value }))}
                  size="small"
                  fullWidth
                >
                  {UFs.map((uf) => (
                    <MenuItem key={uf} value={uf}>
                      {uf}
                    </MenuItem>
                  ))}
                </Select>
              </S.ContentLabelAddress>
            </S.ContentInfoAddress>
            <S.ContentLabelAddress>
              <label style={{ fontSize: 12 }}>Complemento</label>
              <TextField
                placeholder="Complemento"
                value={partnerData.complement || ''}
                onChange={(e) =>
                  setPartnerData((prev) => ({ ...prev, complement: e.target.value }))
                }
                size="small"
                fullWidth
              />
            </S.ContentLabelAddress>
          </>
        )}
      </>
    );
  }

  return null;
};

export default DynamicFieldRenderer;
